import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFormikContext } from 'formik';
import { Button, ButtonType } from 'syngenta-digital-cropwise-react-ui-kit';
import ProductListProvider from 'screens/ProductList/context/ProductListProvider';
import AddProductDrawer from 'screens/ProductList/components/AddProductDrawer';
import DrawerDimmingOverlay from 'components/DrawerDimmingOverlay';
import { mapProductToAdd } from 'components/ProductSideBar/helpers/productHelper';
import {
  getProductType,
  getRegistrationNumber
} from 'screens/ProductList/helpers/filteredProducts';
import EmptyTableState from 'components/EmptyTableState';
import NoProductsSelectedIcon from 'assets/no_products_found.svg';
import PlusIcon from 'assets/plus.svg?react';
import FillProductDetailsDrawer from 'screens/Applications&WorkOrders/StepsLayout/Components/ProductsStep/components/FillProductDetailsDrawer';
import CreateCustomProductDrawer from 'screens/Applications&WorkOrders/StepsLayout/Components/ProductsStep/components/CreateCustomProductDrawer';
import { Product, ProductWithPlanFields } from 'types/product';
import useProduct from 'hooks/useProduct';
import { PlanWizardFormValues, PlanProductLineItem } from '../../types';
import ProductsTable from './components/ProductsTable';
import {
  mapPlanProductToApplicationsProduct,
  mergeApplicationsProductIntoPlanLineItem,
  buildPlanLineItemStubFromCustomProduct,
  generateTrackingId
} from './helpers/planProductMapping';

/** FR-091: the real unique key for one line item — `trackingId` when set, falling back to `productId` for entries added before this fix (which can't have a duplicate-productId sibling anyway). */
const lineItemKey = (item: PlanProductLineItem): string =>
  item.trackingId || item.productId;

/** Matches `useProduct().fetchProductData`'s own resolved-data shape verbatim (see `hooks/useProduct.js`) — camelCase, not `PlanProductLineItem`'s lowercase `producttype`/`registrationnumber`. */
interface ProductCatalogDetails {
  productType?: string;
  stdunit?: string;
  registrationNumber?: string;
}

/** Matches Applications/Work Orders' own `DRAWER_Z_INDEX` layering exactly (FR-064). */
const DRAWER_Z_INDEX = 1000;

interface CatalogProduct {
  id?: string;
  productId?: string;
  productApplicationId?: string;
  name?: string;
  productName?: string;
  manufacturer?: string;
  manufacturerName?: string;
  standardUnit?: string;
  stdunit?: string;
  stdUnit?: string;
  stdpackageunit?: string;
  stdPackageUnit?: string;
  stdfactor?: number;
  stdFactor?: number;
  /** Whether the catalog/inventory item is itself a custom (org-created) product, not Syngenta's master catalog. */
  custom?: boolean;
  [key: string]: unknown;
}

/**
 * Products step of the Plan creation wizard. Reuses Applications&WorkOrders'
 * `AddProductDrawer` + `FillProductDetailsDrawer` verbatim (minus tank/carrier
 * sections, which don't apply to Plans) — only `helpers/planProductMapping.ts`
 * bridges their `Product` shape to Plans' `PlanProductLineItem`.
 */
const ProductsStep: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<PlanWizardFormValues>();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isAddingProducts, setIsAddingProducts] = useState(false);
  const [
    productUnderEdition,
    setProductUnderEdition
  ] = useState<PlanProductLineItem | null>(null);
  // FR-090: controls `FillProductDetailsDrawer`'s own `visible` prop
  // separately from `productUnderEdition` (which controls whether it's
  // mounted at all) — closing flips this to `false` first so the drawer's
  // slide-out transition can play before `productUnderEdition` unmounts it.
  const [isFillDetailsDrawerVisible, setIsFillDetailsDrawerVisible] = useState(
    false
  );
  const [
    isCreateCustomProductDrawerVisible,
    setIsCreateCustomProductDrawerVisible
  ] = useState(false);
  const [myProductsReloadTrigger, setMyProductsReloadTrigger] = useState(0);
  const products = values.planProductEntries;
  // Cleared on every reopen so a stale close-transition timeout can't fire
  // against a drawer that was reopened in the meantime.
  const closeFillDetailsDrawerTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  useEffect(
    () => () => {
      if (closeFillDetailsDrawerTimeoutRef.current) {
        clearTimeout(closeFillDetailsDrawerTimeoutRef.current);
      }
    },
    []
  );

  const { fetchProductData } = useProduct();
  const [catalogDetailsByProductId, setCatalogDetailsByProductId] = useState<
    Record<string, ProductCatalogDetails>
  >({});

  // Existing plan entries lack producttype/registrationnumber (UI-only,
  // stripped before save) and stdunit isn't guaranteed to round-trip, so
  // fetch each selected product's catalog details once and merge them in,
  // same pattern as Invoices' ProductsStep.
  useEffect(() => {
    const missing = products.filter(
      p => !catalogDetailsByProductId[p.productId]
    );
    if (!missing.length) return undefined;

    let cancelled = false;

    Promise.all(
      missing.map(p =>
        fetchProductData({ productId: p.productId, custom: p.custom }).then(
          (result: { data?: ProductCatalogDetails } | null) => ({
            productId: p.productId,
            data: result?.data
          })
        )
      )
    ).then(results => {
      if (cancelled) return;
      const additions: Record<string, ProductCatalogDetails> = {};
      results.forEach(({ productId, data }) => {
        if (data) additions[productId] = data;
      });
      setCatalogDetailsByProductId(prev => ({ ...prev, ...additions }));
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  // FR-053: auto-open the Select Products drawer on mount, matching
  // Applications/Work Orders and Invoices (Plans was the outlier).
  // FR-067: skip this in edit/duplicate mode — a Plan legitimately saved
  // with zero products shouldn't pop the drawer on open, mirroring
  // Applications/Work Orders' own `!isEditMode && !state?.flow` guard.
  useEffect(() => {
    if (products.length === 0 && !values.isEditOrDuplicateMode) {
      setIsDrawerOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEditExisting = (item: PlanProductLineItem) => {
    if (closeFillDetailsDrawerTimeoutRef.current) {
      clearTimeout(closeFillDetailsDrawerTimeoutRef.current);
    }
    setIsAddingProducts(false);
    setProductUnderEdition(item);
    setIsFillDetailsDrawerVisible(true);
  };

  const handleOnEdit = (item: CatalogProduct) => {
    if (closeFillDetailsDrawerTimeoutRef.current) {
      clearTimeout(closeFillDetailsDrawerTimeoutRef.current);
    }
    // "My Products" items only carry `.productId`; catalog search results
    // only carry `.id` — ordering matters or every "My Products" pick
    // collides on the same empty-string catalogId. `productApplicationId` is
    // excluded here since `selectedForDrawer` stamps it with a `trackingId`,
    // which isn't a valid catalog id to re-add from.
    const catalogId = item.id ?? item.productId ?? '';

    // FR-091: picking a row already in the "Selected" section (or an
    // already-added "My Products" row) now always adds a new, independent
    // line item instead of reopening the existing one for editing — the
    // stub built below gets its own `trackingId` precisely so it doesn't
    // collide with (or overwrite) that existing entry.

    // "My Products" list items come back shaped `productName`/`manufacturerName`
    // (not `name`/`manufacturer`); `mapProductToAdd` only ever reads the latter,
    // so without this fallback a product added from "My Products" lands with
    // an empty productName/manufacturerName — matches Invoices'
    // `ProductsStep`'s own `item.name ?? item.productName` normalization
    // (`getProductName`/`getManufacturerName` in
    // `screens/ProductList/helpers/filteredProducts.js` carry the identical
    // fallback for display, confirming this is a real, general inconsistency
    // across product data sources, not specific to this call site).
    const catalogName = item.name ?? item.productName ?? '';
    const catalogManufacturer =
      item.manufacturer ?? item.manufacturerName ?? '';
    // Same inconsistency for the standard unit — `mapProductToAdd` only ever
    // reads `.standardUnit`, but "My Products" items carry it as `.stdunit`
    // (matching `getProductStandardUnit`'s `stdunit ?? stdUnit ?? standardUnit`
    // fallback order in `screens/ProductList/helpers/filteredProducts.js`).
    const catalogStandardUnit =
      item.stdunit ?? item.stdUnit ?? item.standardUnit ?? '';

    // Literal 'plans' (not window.location.pathname) since this call site is
    // always Plans; mapProductToAdd is untyped legacy JS so its result is cast.
    const stub = mapProductToAdd(
      {
        ...item,
        id: catalogId,
        name: catalogName,
        manufacturer: catalogManufacturer,
        standardUnit: catalogStandardUnit
      },
      { area: values.area },
      'plans'
    ) as PlanProductLineItem;

    // `mapProductToAdd`'s 'plans' branch never carries these over, so
    // they're captured here instead — UI-only (fed back into
    // `selectedForDrawer` below so this same catalog item, once added,
    // still shows its real Product Type/EPA Number in the "Selected"
    // section instead of the drawer's blank/`---` fallback); stripped again
    // before the save payload in `buildPlanCreatePayload`.
    stub.producttype = getProductType(item) || '';
    stub.registrationnumber = getRegistrationNumber(item) || '';

    // mapProductToAdd's 'plans' branch sets stdpackageunit to the standard
    // unit and reads a camelCase stdFactor the catalog doesn't send, so a
    // packaged product (e.g. seed sold per 100,000-seed "package") loses its
    // package unit/factor and seed→package conversions throw in the drawer.
    stub.stdunit = catalogStandardUnit;
    stub.stdpackageunit =
      item.stdpackageunit ?? item.stdPackageUnit ?? catalogStandardUnit;
    stub.stdfactor = item.stdfactor ?? item.stdFactor ?? 1;

    // mapProductToAdd hardcodes custom: true, meant only for brand-new custom
    // products. "My Products" can include previously-created custom products
    // too, so read the picked item's own flag instead of forcing it.
    stub.custom = Boolean(item.custom);

    // FR-091: every fresh pick gets its own unique trackingId — the real key
    // `lineItemKey`/`rowKey` use — so a repeat pick of an already-added
    // product becomes an independent line item rather than colliding with
    // (and silently overwriting) the existing one on save.
    stub.trackingId = generateTrackingId();

    // PR6: open FillProductDetailsDrawer for the fresh stub instead of
    // adding it immediately — closes the AddProductDrawer first so both
    // drawers never render open at once.
    setIsDrawerOpen(false);
    setIsAddingProducts(true);
    setProductUnderEdition(stub);
    setIsFillDetailsDrawerVisible(true);
  };

  const handleSetSelectedProducts = (items: CatalogProduct[] = []) => {
    // Keyed on `lineItemKey`, not `productId` — otherwise duplicate picks of
    // the same product collapse into one row.
    const selectedKeys = new Set(
      items.map(i => i.productApplicationId ?? i.id ?? i.productId ?? '')
    );
    setFieldValue(
      'planProductEntries',
      products.filter(p => selectedKeys.has(lineItemKey(p)))
    );
  };

  const handleRemove = (key: string) => {
    setFieldValue(
      'planProductEntries',
      products.filter(p => lineItemKey(p) !== key)
    );
  };

  // Keep the detail drawer mounted during its close animation. While adding a
  // product, the selection drawer slides back over it before cleanup.
  const closeFillDetailsDrawer = () => {
    if (isAddingProducts) {
      setIsDrawerOpen(true);
      closeFillDetailsDrawerTimeoutRef.current = setTimeout(() => {
        setProductUnderEdition(null);
        setIsAddingProducts(false);
        closeFillDetailsDrawerTimeoutRef.current = null;
      }, 300);
      return;
    }

    setIsFillDetailsDrawerVisible(false);
    closeFillDetailsDrawerTimeoutRef.current = setTimeout(() => {
      setProductUnderEdition(null);
      closeFillDetailsDrawerTimeoutRef.current = null;
    }, 300);
  };

  const handleSaveProductDetails = (product: ProductWithPlanFields) => {
    if (!productUnderEdition) return;
    const merged = mergeApplicationsProductIntoPlanLineItem(
      productUnderEdition,
      product
    );

    const alreadyInList = products.some(
      p => lineItemKey(p) === lineItemKey(merged)
    );
    setFieldValue(
      'planProductEntries',
      alreadyInList
        ? products.map(p =>
            lineItemKey(p) === lineItemKey(merged) ? merged : p
          )
        : [...products, merged]
    );
    closeFillDetailsDrawer();
  };

  const handleCustomProductCreated = (product: Product) => {
    if (closeFillDetailsDrawerTimeoutRef.current) {
      clearTimeout(closeFillDetailsDrawerTimeoutRef.current);
    }
    setIsCreateCustomProductDrawerVisible(false);
    setIsAddingProducts(true);
    setProductUnderEdition(
      buildPlanLineItemStubFromCustomProduct(product, values.area)
    );
    setIsFillDetailsDrawerVisible(true);
    // FR-064: bumps AddProductDrawer's "My Products" list to refetch, so a
    // just-created custom product shows up immediately instead of only
    // after a manual search/reload.
    setMyProductsReloadTrigger(prev => prev + 1);
  };

  const selectedForDrawer = useMemo(
    () =>
      products.map(p => {
        const details = catalogDetailsByProductId[p.productId];
        return {
          id: p.productId,
          // Lets duplicate picks of the same product be told apart when
          // deselected in the drawer.
          productApplicationId: lineItemKey(p),
          name: p.productName,
          manufacturer: p.manufacturerName || '',
          producttype: p.producttype || details?.productType || '',
          registrationnumber:
            p.registrationnumber || details?.registrationNumber || '',
          stdunit: p.stdunit || details?.stdunit || '',
          stdpackageunit: p.stdpackageunit,
          stdfactor: p.stdfactor,
          isInUse: true
        };
      }),
    [products, catalogDetailsByProductId]
  );

  // FR-046 (2026-08-17 design QA): label/style now match Work Orders' own
  // `AddProductsButton` exactly — "Select Products", filled while the table
  // is empty, ghost/outline (green) once at least one product has been
  // added — replacing the previous always-filled "Add Products" treatment.
  const hasProducts = products.length > 0;
  const addProductButton = (
    <Button
      style={{ background: hasProducts ? 'white' : undefined }}
      type={ButtonType.primary}
      ghost={hasProducts}
      data-testid="add-products-button"
      icon={<PlusIcon fill={hasProducts ? '#14803C' : 'white'} />}
      onClick={() => setIsDrawerOpen(true)}
    >
      Select Products
    </Button>
  );

  return (
    <div data-testid="plan-products-step">
      {products.length === 0 ? (
        <div className="bg-white shadow-sm-card rounded-lg w-full">
          <EmptyTableState
            title="No Products Selected"
            message="Add products to your Plan."
            overlayIcon={NoProductsSelectedIcon}
            button={addProductButton}
          />
        </div>
      ) : (
        <>
          <div className="flex justify-end mb-2">{addProductButton}</div>
          <div className="bg-white shadow-sm-card rounded-lg w-full">
            <ProductsTable
              products={products}
              onRemove={handleRemove}
              onEdit={handleEditExisting}
            />
          </div>
        </>
      )}

      {/* FR-064: a single persistent overlay dims the background across the
          Select-Products→Fill-Details handoff, instead of each drawer's own
          mask independently dimming/undimming and visibly flickering. */}
      <DrawerDimmingOverlay
        visible={isDrawerOpen || !!(productUnderEdition && isAddingProducts)}
        zIndex={DRAWER_Z_INDEX - 2}
        onClick={
          isDrawerOpen && !productUnderEdition
            ? () => setIsDrawerOpen(false)
            : undefined
        }
      />

      <ProductListProvider>
        <AddProductDrawer
          showProductList
          hideBackButton
          zIndex={DRAWER_Z_INDEX}
          mask={false}
          visible={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          submitButtonText="Done"
          submitButtonType={ButtonType.outline}
          myProductsReloadTrigger={myProductsReloadTrigger}
          selectedProductsList={selectedForDrawer}
          setSelectedProductsList={handleSetSelectedProducts}
          handleOnEdit={handleOnEdit}
          handleAddProductsSubmit={() => setIsDrawerOpen(false)}
          handleOnAddCustomProduct={() => {
            setIsCreateCustomProductDrawerVisible(true);
            setIsDrawerOpen(false);
          }}
        />

        {productUnderEdition && (
          <FillProductDetailsDrawer
            visible={isFillDetailsDrawerVisible}
            isEditing={!isAddingProducts}
            suppressAnimation={isAddingProducts}
            zIndex={DRAWER_Z_INDEX - 1}
            mask={!isAddingProducts}
            showApplicationCount
            showTimingFields
            product={mapPlanProductToApplicationsProduct(
              productUnderEdition,
              !isAddingProducts
            )}
            onClose={closeFillDetailsDrawer}
            onSave={handleSaveProductDetails}
          />
        )}

        {isCreateCustomProductDrawerVisible && (
          <CreateCustomProductDrawer
            visible
            zIndex={DRAWER_Z_INDEX - 1}
            onClose={() => {
              setIsDrawerOpen(true);
              setIsCreateCustomProductDrawerVisible(false);
            }}
            onSuccess={handleCustomProductCreated}
          />
        )}
      </ProductListProvider>
    </div>
  );
};

export default ProductsStep;
